import { ColumnDatatable, FiltersDatatableList } from '@europrocurement/flexy-datatable';
import { type DossierTaches } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquare, faSquareCheck } from '@fortawesome/sharp-regular-svg-icons';
import { Box, Typography } from '@mui/material';
import { type PrescripteurTaches } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { type UtilisateurTaches } from '@europrocurement/l2d-domain';
import { REALM_RESP_FORMALIST, UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { Link } from 'react-router-dom';
import { TaskPriorityChip } from '../components';
import {
    type DossierNumero,
    PeoplePreview,
    type ContactItem,
    useDossierNumero,
    dossiersBasePath,
} from '../../Dossiers';
import useTasksHook, { FormalistItem } from './useTasksHook';
import { USER_DEFAULT_FORMALISTE } from '../../Utilisateur';
import { tasksPriorities } from '../constant';
import usePrescribers, {
    type LitePrescripteurJson,
} from '../../PrescriberClient/hooks/usePrescribers';
import TaskActionMenu from '../components/TaskActionMenu/TaskActionMenu';
import { prescripteursBasePath } from '../../PrescriberClient/constants/paths';

type UseTasksTableHookProps = {
    apiTarget?: 'formalite' | 'tiers' | 'utilisateur';
};

const useTasksTableHook = ({ apiTarget = 'formalite' }: UseTasksTableHookProps) => {
    // the params of useTaskHook are not used for the method onSearchUser
    const { onSearchUsers, handleCheckTask } = useTasksHook({
        apiTarget,
        linkedEntityId: '',
    });
    const { searchDossierNumero } = useDossierNumero();
    const { searchPrescriber } = usePrescribers();
    const keycloakService = UseKeycloakService();
    const isFormalistManager = keycloakService.checkRole(REALM_RESP_FORMALIST);

    const defaultTachesColumns: ColumnDatatable<
        DossierTaches | PrescripteurTaches | UtilisateurTaches
    >[] = [
        {
            label: 'Titre',
            render: ({ tache }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {tache?.title}
                </Typography>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'tacheTitle',
        },
        {
            label: 'Assigné à',
            render: ({ tache }) => {
                if (tache?.assignTo?.id === USER_DEFAULT_FORMALISTE) {
                    return '';
                }

                return (
                    <PeoplePreview
                        firstName={tache?.assignTo?.prenom as string}
                        lastName={tache?.assignTo?.nom as string}
                        userIdentifier={tache?.assignTo?.userIdentifier as string}
                    />
                );
            },
            isDisplayed: true,
            sortable: true,
            headerTooltip: 'Formaliste',
            field: 'tacheAssignTo',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
                width: '64px',
            },
        },
        {
            label: 'Créé par',
            render: ({ tache }) => {
                if (tache?.createdBy?.id === USER_DEFAULT_FORMALISTE) {
                    return '';
                }

                return (
                    <PeoplePreview
                        firstName={tache?.createdBy?.prenom as string}
                        lastName={tache?.createdBy?.nom as string}
                        userIdentifier={tache?.createdBy?.userIdentifier as string}
                    />
                );
            },
            isDisplayed: true,
            sortable: false,
            headerTooltip: 'Formaliste',
            field: 'tacheCreatedBy',
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
                width: '64px',
            },
        },
        {
            label: 'Date limite',
            render: ({ tache }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {tache?.expiredAt ? new Date(tache.expiredAt).toLocaleDateString() : ''}
                </Typography>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'tacheExpiredAt',
        },
        {
            label: 'Date de création',
            render: ({ tache }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {tache?.createdAt ? new Date(tache.createdAt).toLocaleDateString() : ''}
                </Typography>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'tacheCreatedAt',
        },
        {
            label: 'Priorité',
            render: ({ tache }) => <TaskPriorityChip taskPriority={tache?.priority} />,
            isDisplayed: true,
            sortable: true,
            field: 'tachePriority',
        },
        {
            label: 'Terminée',
            render: (task) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FontAwesomeIcon
                        icon={task.tache?.isDone ? faSquareCheck : faSquare}
                        style={{ cursor: 'pointer' }}
                        onClick={(e: React.MouseEvent) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (task && task.tache) {
                                handleCheckTask(task);
                            }
                        }}
                    />
                    <Typography variant="l2dtableCell">
                        {task.tache?.isDone ? 'Terminée' : 'A faire'}
                    </Typography>
                </Box>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'tacheIsDone',
        },
        {
            label: 'Actions',
            displayLabel: false,
            render: (item: DossierTaches | PrescripteurTaches | UtilisateurTaches) => (
                <TaskActionMenu item={item} />
            ),
            isDisplayed: true,
            sortable: false,
            sx: {
                display: {
                    xs: 'none',
                    sm: 'none',
                    md: 'table-cell',
                    lg: 'table-cell',
                    xl: 'table-cell',
                },
            },
        },
    ];

    const defaultFilters: FiltersDatatableList = [
        { label: 'Titre', field: 'tacheTitle', type: 'text' },
        { label: 'Terminée', field: 'tacheIsDone', type: 'boolean' },
        {
            field: 'tachePriority',
            label: 'Priorité',
            type: 'staticSelect',
            icon: 'exclamation-circle',
            renderFilterListIcon: 'exclamation-circle',
            options: tasksPriorities.map((taskPrio) => ({
                value: taskPrio.value.toString(),
                label: taskPrio.label,
            })),
        },
        {
            label: 'Assigné à',
            field: 'tacheAssignTo',
            icon: 'circle-user',
            type: 'selectApi',
            searchFunction: onSearchUsers,
            renderItem: (item: FormalistItem) => item.fullName,
            valueMap: (item: ContactItem) => item.id,
        },
        {
            label: 'Créé par',
            field: 'tacheCreatedBy',
            icon: 'circle-user',
            type: 'selectApi',
            searchFunction: onSearchUsers,
            renderItem: (item: FormalistItem) => item.fullName,
            valueMap: (item: FormalistItem) => item.id,
        },
        {
            type: 'dateRange',
            label: 'Date limite',
            field: 'tacheExpiredAt',
            icon: 'calendar',
            displayFormat: (startDate: Date, endDate: Date) =>
                `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
        },
        {
            type: 'dateRange',
            label: 'Date de création',
            field: 'tacheCreatedAt',
            icon: 'calendar',
            displayFormat: (startDate: Date, endDate: Date) =>
                `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`,
        },
    ];

    const getColumnsTachesPrescripteurs = (): ColumnDatatable<PrescripteurTaches>[] => [
        {
            label: 'Prescripteur',
            render: ({ tiers }) => (
                <Link
                    style={{
                        textDecoration: 'none',
                        color: 'unset',
                        textWrap: 'nowrap',
                    }}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                    }}
                    to={`/${prescripteursBasePath}/liste/${tiers.id}`}
                    target="_blank"
                >
                    <Typography variant="l2dcaption">{tiers.raisonSociale}</Typography>
                </Link>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'tiersRaisonSociale',
        },
        ...defaultTachesColumns,
    ];

    const getColumnsTachesDossier = (): ColumnDatatable<DossierTaches>[] => [
        {
            label: 'Numéro de dossier',
            render: ({ dossier }) => (
                <Link
                    style={{
                        textDecoration: 'none',
                        color: 'unset',
                        textWrap: 'nowrap',
                    }}
                    onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                    }}
                    to={`/${dossiersBasePath}/${dossier.id}?dossier-detail=activities`}
                    target="_blank"
                >
                    <Typography variant="l2dcaption">#{dossier.numero}</Typography>
                </Link>
            ),
            isDisplayed: true,
            sortable: true,
            field: 'dossierNumero',
        },
        {
            label: 'Société',
            render: ({ dossier }) => (
                <Typography
                    variant="l2dtableCell"
                    color="text.gris70"
                >
                    {dossier.societeDenomination}
                </Typography>
            ),
            isDisplayed: true,
            sortable: false,
            field: 'dossierSociete',
        },
        ...defaultTachesColumns,
    ];

    const getFiltersTachesPrescripteurs = (): FiltersDatatableList => [
        {
            label: 'Prescripteurs',
            field: 'tachePrescripteurIri',
            type: 'selectApi',
            icon: 'user',
            searchFunction: searchPrescriber,
            renderItem: (item: LitePrescripteurJson) => item.raisonSociale,
            valueMap: (item: LitePrescripteurJson) => item.id,
        },
        ...defaultFilters,
    ];

    const getFiltersTachesDossier = (): FiltersDatatableList => [
        {
            label: 'Dossier',
            field: 'dossierNumero',
            type: 'selectApi',
            icon: 'folder',
            searchFunction: searchDossierNumero,
            renderItem: (item: DossierNumero) => item.numero as string,
            valueMap: (item: DossierNumero) => item.numero,
        },
        ...defaultFilters,
    ];

    // Columns / filters utilisateur are filtered based on current user role
    const getColumnsTachesUtilisateur = (): ColumnDatatable<UtilisateurTaches>[] => {
        if (isFormalistManager) {
            return defaultTachesColumns;
        }
        return defaultTachesColumns.filter(
            (column) => !['tacheAssignTo'].includes(column.field as string),
        );
    };
    const getFiltersTachesUtilisateur = (): FiltersDatatableList => {
        if (isFormalistManager) {
            return defaultFilters;
        }
        return defaultFilters.filter(
            (filter) => !['tacheAssignTo'].includes(filter.field as string),
        );
    };

    return {
        getColumnsTachesPrescripteurs,
        getFiltersTachesPrescripteurs,
        getColumnsTachesDossier,
        getFiltersTachesDossier,
        getColumnsTachesUtilisateur,
        getFiltersTachesUtilisateur,
    };
};

export default useTasksTableHook;
